import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from '~/utils/navigation';

import { selectLimitsModalData, updateLimitsModal } from '~/components/account/ducks';
import Icon, { ICON_CLOSE } from '~/components/icon';
import { MainTitle } from '~/components/misc/section-head';
import useTranslations from '~/components/translation/use-translations';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import usePaymentTracking from '~/components/payment-beta/deposit/hooks/use-payment-tracking';

import { textKeys, getLimitsContent, getLimitPopupTracking } from './constants';
import { LimitType, LIMIT_POPUP_ACTIONS } from '~/components/limits/types';
import {
	ButtonOutlinedCustom,
	ButtonContainer,
	CloseButton,
	CloseIconWrapper,
	LimitContainer,
	SubTitle
} from './styled';

const RGLimit = () => {
	const dispatch = useDispatch();
	const router = useRouter();

	const { trackEvent } = usePaymentTracking();
	const { translations: t } = useTranslations(textKeys);
	const { responsibleGamingLimitType } = useSelector(selectLimitsModalData) ?? {};
	const transformPath = useContextualPathTransformer();

	const limitContent = useMemo(() => getLimitsContent(responsibleGamingLimitType), [responsibleGamingLimitType]);
	const hasTracked = useRef(false);

	useEffect(() => {
		if (!hasTracked.current && responsibleGamingLimitType) {
			trackEvent(getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.VIEW));
			hasTracked.current = true;
		}
	}, [responsibleGamingLimitType, trackEvent]);

	const onClose = useCallback(
		(informMobileApps = true) => {
			dispatch(updateLimitsModal(false, {}, informMobileApps));
		},
		[dispatch]
	);

	const onClickClose = useCallback(() => {
		trackEvent(getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.CLOSE));
		onClose();
	}, [onClose, responsibleGamingLimitType, trackEvent]);

	const onClick = useCallback(() => {
		trackEvent(getLimitPopupTracking(responsibleGamingLimitType as LimitType, LIMIT_POPUP_ACTIONS.CTA_CLICK));
		router.push(transformPath(limitContent.navigateUrl));
		onClose(false);
	}, [trackEvent, responsibleGamingLimitType, router, transformPath, limitContent.navigateUrl, onClose]);

	return (
		<LimitContainer data-at="spending-screen">
			<MainTitle padding="10px 18px">{t[limitContent.title]}</MainTitle>
			<SubTitle>{t[limitContent.subTitle]}</SubTitle>
			<CloseButton onClick={onClickClose}>
				<CloseIconWrapper>
					<Icon type={ICON_CLOSE} />
				</CloseIconWrapper>
			</CloseButton>
			<ButtonContainer>
				<ButtonOutlinedCustom data-at="spending-limit-modal" onClick={onClick} $customFont="var(--h3)">
					{t[limitContent.buttonText]}
				</ButtonOutlinedCustom>
			</ButtonContainer>
		</LimitContainer>
	);
};

export default RGLimit;
